// Route table. Sections are code-split (one lazy chunk per section).
// A section is src/routes/<Section>.tsx (default export) with its nav entry in
// src/routes/<Section>.nav.ts (path, place, loader; shell/nav.ts). Labels come from i18n nav.
import { lazy, type ComponentType, type LazyExoticComponent } from 'react';
import type { RouteObject } from 'react-router';
import { AdminOnly } from './AdminOnly';
import { Layout } from './Layout';
import { Login } from './Login';
import { useApp } from '../state/store';
import { AppError, RouteError } from './RouteError';
import { INSTALL_PATH, MORE_PATH, NAV, type NavId } from './nav';
import { LOADERS, loadInstall, loadMore, pageLoaders, pagesWarmed } from './page-loaders';
import { afterReadyAndPaint, slowNetwork, whenControlled } from './startup';

type Page = LazyExoticComponent<ComponentType>;

// Importers (shell/page-loaders.ts), kept separate so the shell can warm every chunk (the loading
// screen's sections step, else preloadPages): a tab then opens at once instead of waiting for its
// chunk (offline, or on a slow phone).
export const PAGES = Object.fromEntries((Object.keys(LOADERS) as NavId[]).map((id) => [id, lazy(LOADERS[id])])) as Record<NavId, Page>;

const More = lazy(loadMore);
const Install = lazy(loadInstall);
const NotFound = lazy(() => import('../routes/NotFound'));
// Development only: the layout v2 primitives on one page (dev/SpreadDemo.tsx); not in a build.
const SpreadDemo = import.meta.env.DEV ? lazy(() => import('../dev/SpreadDemo')) : null;
// Development only: Ääntämisvalmentaja's platform bench for the phones over npm run dev:https (coach/dev/CoachLab.tsx).
const CoachLab = import.meta.env.DEV ? lazy(() => import('../coach/dev/CoachLab')) : null;

/** Fetch and evaluate every section chunk in the background (idempotent; errors ignored). */
let preloaded = false;
export function preloadPages(): void {
  if (preloaded || pagesWarmed()) return;
  preloaded = true;
  // One at a time, so the warm-up never competes with what the user is doing.
  void pageLoaders().reduce<Promise<unknown>>((p, load) => p.then(() => load().catch(() => undefined)), Promise.resolve());
}

// Warm the chunks once start-up is over (not in unit tests): never while the bundle is still
// downloading or before the first screen has painted. On the very first visit the service worker
// is downloading the same chunks into its precache, so the page waits until the worker controls
// it and then reads them from there; on a slow or metered connection without a worker it skips the
// warm-up (each section loads its own chunk when opened).
// Since 2026-10-10 the loading screen does all this on a cold start (shell/warmup/, sections.warmup.ts,
// dictionary/dictionary.warmup.ts); this stays as the fallback and has nothing left to do after it.
if (typeof window !== 'undefined' && import.meta.env.MODE !== 'test') {
  const idle = (f: () => void) => (typeof window.requestIdleCallback === 'function' ? window.requestIdleCallback(f, { timeout: 5000 }) : setTimeout(f, 0));
  // Then the lexicon and its search index (not the form index): the dictionary's first search
  // is instant even on the first screen that opens it.
  const lexicon = () => idle(() => void import('../lib/lexicon').then((m) => m.startLexicon({ forms: false })).catch(() => undefined));
  void afterReadyAndPaint().then(async () => {
    const hasWorker = import.meta.env.PROD && 'serviceWorker' in navigator;
    const controlled = hasWorker ? await whenControlled() : false;
    if (!controlled && slowNetwork()) {
      lexicon();
      return;
    }
    window.setTimeout(
      () =>
        idle(() => {
          preloadPages();
          lexicon();
        }),
      controlled ? 1000 : 2500,
    );
  });
}

// Every route has an error screen (RouteError, inside the shell); the root has its own (AppError)
// for a crash in the shell itself. Without them React Router shows its developer error page.
const routeError = <RouteError />;

/** The app, or the login screen while this device has no valid session (store.needsLogin). */
function Root() {
  return useApp((s) => s.needsLogin) ? <Login /> : <Layout />;
}

export const routes: RouteObject[] = [
  {
    element: <Root />,
    errorElement: <AppError />,
    children: [
      ...NAV.map((n): RouteObject => {
        const C = PAGES[n.id];
        const element = n.admin ? (
          <AdminOnly fallback={<NotFound />}>
            <C />
          </AdminOnly>
        ) : (
          <C />
        );
        return n.path === '/' ? { index: true, element, errorElement: routeError } : { path: `${n.path.slice(1)}/*`, element, errorElement: routeError };
      }),
      { path: MORE_PATH.slice(1), element: <More />, errorElement: routeError },
      { path: INSTALL_PATH.slice(1), element: <Install />, errorElement: routeError },
      ...(SpreadDemo ? [{ path: 'dev/spread', element: <SpreadDemo />, errorElement: routeError }] : []),
      ...(CoachLab ? [{ path: 'dev/valmentaja', element: <CoachLab />, errorElement: routeError }] : []),
      { path: '*', element: <NotFound />, errorElement: routeError },
    ],
  },
];
